import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { pagesAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileText, Eye, EyeOff } from 'lucide-react';
import { slugify } from '../../lib/utils';

const emptyPage = {
  title: '',
  slug: '',
  content: '',
  meta_title: '',
  meta_description: '',
  is_published: true,
};

export const AdminPages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState(emptyPage);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await pagesAPI.getAll();
      setPages(response.data);
    } catch (e) {
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingPage(null);
    setFormData(emptyPage);
    setDialogOpen(true);
  };

  const openEditDialog = (page) => {
    setEditingPage(page);
    setFormData(page);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingPage) {
        await pagesAPI.update(editingPage.id, formData);
        toast.success('Page updated!');
      } else {
        await pagesAPI.create(formData);
        toast.success('Page created!');
      }
      setDialogOpen(false);
      fetchPages();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    try {
      await pagesAPI.delete(id);
      toast.success('Page deleted!');
      fetchPages();
    } catch (e) {
      toast.error('Failed to delete page');
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-generate slug from title
      if (field === 'title' && !editingPage) {
        updated.slug = slugify(value);
      }
      return updated;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-pages">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Pages</h1>
          <p className="mt-2 text-slate-600">Create and manage additional pages</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} data-testid="add-page-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Page
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPage ? 'Edit Page' : 'Add Page'}</DialogTitle>
              <DialogDescription>Create content pages for your site</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Page Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => updateField('title', e.target.value)}
                    placeholder="Privacy Policy"
                    data-testid="page-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL Slug</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => updateField('slug', e.target.value)}
                    placeholder="privacy-policy"
                  />
                  <p className="text-xs text-slate-500">
                    URL: /page/{formData.slug || 'your-slug'}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Content (HTML supported)</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => updateField('content', e.target.value)}
                  placeholder="<h2>Your content here...</h2><p>Write your page content...</p>"
                  rows={12}
                  className="font-mono text-sm"
                  data-testid="page-content-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Meta Title (SEO)</Label>
                  <Input
                    value={formData.meta_title}
                    onChange={(e) => updateField('meta_title', e.target.value)}
                    placeholder="Privacy Policy | Your Site"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Meta Description (SEO)</Label>
                  <Input
                    value={formData.meta_description}
                    onChange={(e) => updateField('meta_description', e.target.value)}
                    placeholder="Learn about our privacy policy..."
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_published}
                  onCheckedChange={(checked) => updateField('is_published', checked)}
                />
                <Label>Published</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid="save-page-btn">
                  {saving ? 'Saving...' : 'Save Page'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {pages.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-slate-400" />
            <p className="mt-4 text-slate-600">No pages yet. Create your first page!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {pages.map((page) => (
            <Card key={page.id}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <FileText className="w-8 h-8 text-slate-400" />
                    <div>
                      <h3 className="font-semibold text-slate-900">{page.title}</h3>
                      <p className="text-sm text-slate-500">/page/{page.slug}</p>
                    </div>
                    {page.is_published ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                        <Eye className="w-3 h-3" />
                        Published
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
                        <EyeOff className="w-3 h-3" />
                        Draft
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(`/page/${page.slug}`, '_blank')}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEditDialog(page)}>
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(page.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPages;
